import { useState } from 'react';
import PropTypes from 'prop-types';

import SwipeableViews from 'react-swipeable-views';
import { mod } from 'react-swipeable-views-core';
import { virtualize } from 'react-swipeable-views-utils';

import { legacyTheme, styled } from '@prose-ui/legacy';

import { ReactComponent as ArrowLeftIcon } from 'assets/images/icon_arrow_left_no_tail.svg';
import { ReactComponent as ArrowRightIcon } from 'assets/images/icon_arrow_right_no_tail.svg';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const VirtualizeSwipeableViews = virtualize(SwipeableViews);

const IconWrapper = styled.button`
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  display: flex;
`;

const LeftIcon = styled(ArrowLeftIcon)`
  margin-right: 20px;
  path {
    stroke: white;
  }
`;

const RightIcon = styled(ArrowRightIcon)`
  margin-left: 20px;
  path {
    stroke: white;
  }
`;

const Dot = styled.div`
  width: 9px;
  height: 9px;
  border-radius: 9px;
  background-color: white;
  margin: 9px;
  opacity: ${({ selected }) => (selected ? 1 : 0.25)};
`;

const DotWrapper = styled.div`
  position: absolute;
  bottom: 40px;
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
`;

const Wrapper = styled.div`
  height: 100%;
  padding: 50px 0px;
  ${legacyTheme.breakpoints.up('md')} {
    padding-top: 0px;
  }
`;

const InfiniteCarousel = ({ cardsLength, slideRender, dataFrom, dataClick }) => {
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [dotIndex, setDotIndex] = useState(0);
  const { isDesktop } = useResponsiveVariant();

  const slideRenderer = params => {
    const { index, key } = params;
    const virtualIndex = mod(index, cardsLength);
    const onClick = () => {
      setDotIndex(virtualIndex);
      setCarouselIndex(index);
    };

    return slideRender(index, virtualIndex, carouselIndex, onClick, key);
  };

  const onChangeIndex = index => {
    if (index > carouselIndex) {
      if (dotIndex + 1 >= cardsLength) {
        setDotIndex(0);
      } else {
        setDotIndex(oldValue => oldValue + 1);
      }
    } else if (dotIndex - 1 < 0) {
      setDotIndex(cardsLength - 1);
    } else {
      setDotIndex(oldValue => oldValue - 1);
    }
    setCarouselIndex(index);
  };

  const onClickLeftIcon = () => {
    if (dotIndex - 1 < 0) {
      setDotIndex(cardsLength - 1);
    } else {
      setDotIndex(oldValue => oldValue - 1);
    }
    setCarouselIndex(oldValue => oldValue - 1);
  };

  const onClickRightIcon = () => {
    if (dotIndex + 1 >= cardsLength) {
      setDotIndex(0);
    } else {
      setDotIndex(oldValue => oldValue + 1);
    }
    setCarouselIndex(oldValue => oldValue + 1);
  };

  return (
    <Wrapper>
      <VirtualizeSwipeableViews
        containerStyle={{ height: '100%', alignItems: isDesktop ? 'center' : 'flex-start' }}
        enableMouseEvents
        index={carouselIndex}
        onChangeIndex={onChangeIndex}
        slideRenderer={slideRenderer}
        style={{
          height: '100%',
          padding: isDesktop ? '0px 32%' : '0px 50px 0px 0px',
        }}
      />
      <DotWrapper>
        <IconWrapper
          aria-label="go to previous slide"
          data-click={dataClick}
          data-from={dataFrom}
          onClick={onClickLeftIcon}
          tabindex="-1"
        >
          <LeftIcon />
        </IconWrapper>
        {[...Array(cardsLength).keys()].map(cardIndex => (
          <Dot key={`${cardIndex}-dot`} selected={dotIndex === cardIndex} />
        ))}
        <IconWrapper
          aria-label="go to next slide"
          data-click={dataClick}
          data-from={dataFrom}
          onClick={onClickRightIcon}
          tabindex="0"
        >
          <RightIcon />
        </IconWrapper>
      </DotWrapper>
    </Wrapper>
  );
};

InfiniteCarousel.propTypes = {
  dataFrom: PropTypes.string.isRequired,
  dataClick: PropTypes.string.isRequired,
  cardsLength: PropTypes.number.isRequired,
  slideRender: PropTypes.func.isRequired,
};

export default InfiniteCarousel;
